import { Button, CircularProgress, Stack, useTheme } from "@mui/material";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";

/**
 * The Google + Apple button pair, shared by the auth screen and the
 * onboarding auth sheet. Apple first and solid (iOS platform convention;
 * required alongside Google on iOS per guideline 4.8) — native uses the OS
 * sheet (bundle-id audience), web uses the Services ID redirect flow
 * (io.insightanalytics.auth, configured 2026-07-13).
 */

interface SocialAuthButtonsProps {
  onGoogle: () => void;
  onApple: () => void;
  isLoading: boolean;
}

export const SocialAuthButtons = ({
  onGoogle,
  onApple,
  isLoading,
}: SocialAuthButtonsProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Stack spacing={1.5}>
      <Button
        fullWidth
        startIcon={!isLoading && <AppleIcon />}
        onClick={onApple}
        disabled={isLoading}
        sx={{
          height: 56,
          fontWeight: 700,
          fontSize: "1rem",
          textTransform: "none",
          borderRadius: 2.5,
          backgroundColor: isDark ? "#ffffff" : "#1d1d1f",
          color: isDark ? "#1d1d1f" : "#ffffff",
          transition: "background-color 0.15s ease, transform 0.1s ease",
          "&:hover": {
            backgroundColor: isDark ? "rgba(255,255,255,0.9)" : "#000000",
            transform: "translateY(-1px)",
          },
          "&:active": { transform: "scale(0.98)" },
          "&:disabled": {
            backgroundColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
            color: isDark ? "#1d1d1f" : "#ffffff",
          },
        }}
      >
        {isLoading && <CircularProgress size={24} color="inherit" />}
        {!isLoading && "Continue with Apple"}
      </Button>
      <Button
        fullWidth
        variant="outlined"
        startIcon={!isLoading && <GoogleIcon />}
        onClick={onGoogle}
        disabled={isLoading}
        sx={{
          height: 56,
          fontWeight: 700,
          fontSize: "1rem",
          textTransform: "none",
          borderRadius: 2.5,
          color: theme.palette.text.primary,
          borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)",
          transition: "background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease",
          "&:hover": {
            borderColor: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
            transform: "translateY(-1px)",
          },
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        {isLoading && <CircularProgress size={24} color="inherit" />}
        {!isLoading && "Continue with Google"}
      </Button>
    </Stack>
  );
};
